/**
 * Реестр транспорта фирмы: список, добавление по VIN и реквизиту техпаспорта,
 * вывод из реестра.
 *
 * Список показывает **каждую** запись, как она пришла, — выведенные тоже:
 * вывод закрывает запись датой, а не удаляет её, и исчезновение записи было бы
 * дефектом, а не аккуратностью. Ни отбора, ни сортировки — по техническому
 * состоянию в первую очередь: «машина в ремонте на рейс не назначается» есть
 * серверный guard, а не вид экрана.
 *
 * Проверок формата VIN и условной обязательности типа кузова здесь нет ни
 * одной: клиент отправляет введённое, годность решает сервер, и его отказ
 * ложится сообщением под своим полем. Повтор своего VIN клиент не подавляет —
 * идемпотентность по естественному ключу решает сервер ответом `200`.
 *
 * Отказ `422` на VIN, занятом чужой фирмой, **не** ведёт к заявке сам: путь к
 * заявке — отдельное действие человека, и оно здесь ссылкой, а не выводом из
 * кода отказа.
 *
 * Вид, тип кузова, состояние и статус показаны подписью по ключу строителя:
 * перечни значений порождаются из контракта, и второго их экземпляра нет.
 * Пустой тип кузова тягача — пустое место, а не «неизвестно»: поле пришло и
 * законно пусто; «неизвестно» получает только значение вне перечня (CR-007).
 *
 * Блок `WEB-D.2`, решение `Б5`: форма добавления лежит в `details.disclosure`.
 * Прочитанный реестр с машинами оставляет блок закрытым, пустой — открытым;
 * дальше блоком распоряжается человек. Отказ при добавлении блок раскрывает.
 *
 * Решение `Б12`: удавшееся добавление очищает поля ввода, выбранные вид и тип
 * кузова остаются для следующей машины; экран говорит, новая это запись
 * или уже бывшая в показанном списке. Решает запись ответа, а не код ответа.
 */

import { ApiFailure } from '../api/client';
import type { ApiClient } from '../api/client';
import type { Vehicle, VehicleCreateRequest } from '../api/contract';
import { BODY_TYPES, VEHICLE_KINDS } from '../api/generated/enums';
import {
  activeLocale,
  bodyTypeStringKey,
  repairStateStringKey,
  vehicleKindStringKey,
  vehicleStatusStringKey,
} from '../i18n';
import type { Strings } from '../i18n';
import { vehiclePath, vehicleServicePath, vinClaimsPath } from '../router';
import {
  createFieldMessageSlot,
  enumLabel,
  momentLabel,
  renderFieldMessages,
  renderScreenState,
  screenStateFor,
  selectEnumOption,
} from './state';
import type { ScreenState } from './state';

export interface FleetScreenOptions {
  readonly client: ApiClient;
  readonly strings: Strings;
  readonly companyId: string;
}

/** Введённое человеком — ровно поля тела добавления, и ни одного сверх. */
export interface AddVehicleInput {
  readonly kind: string;
  readonly vin: string;
  readonly techPassportNumber: string;
  readonly bodyType?: string | null;
}

export interface FleetScreen {
  readonly element: HTMLElement;
  readonly state: ScreenState | null;
  readonly vehicles: readonly Vehicle[];
  load(): Promise<void>;
  addVehicle(input: AddVehicleInput): Promise<void>;
  withdraw(vehicleId: string): Promise<void>;
}

/** Вид уведомления об удавшемся добавлении: не цвет отказа (`Б12`). */
const DONE_NOTICE_CLASS = 'field-message--done';

export function createFleetScreen(options: FleetScreenOptions): FleetScreen {
  const { client, strings, companyId } = options;

  const element = document.createElement('section');
  const heading = document.createElement('h1');
  const addBlock = document.createElement('details');
  const addCaption = document.createElement('summary');
  const form = document.createElement('form');
  const kindCaption = document.createElement('label');
  const kindCaptionText = document.createElement('span');
  const kindField = document.createElement('select');
  const kindMessage = createFieldMessageSlot();
  const vinCaption = document.createElement('label');
  const vinCaptionText = document.createElement('span');
  const vinField = document.createElement('input');
  const vinMessage = createFieldMessageSlot();
  const techCaption = document.createElement('label');
  const techCaptionText = document.createElement('span');
  const techField = document.createElement('input');
  const techMessage = createFieldMessageSlot();
  const bodyCaption = document.createElement('label');
  const bodyCaptionText = document.createElement('span');
  const bodyField = document.createElement('select');
  const bodyMessage = createFieldMessageSlot();
  const submit = document.createElement('button');
  const claimsLink = document.createElement('a');
  const list = document.createElement('ul');
  const emptyNotice = document.createElement('p');
  /** Место сообщения сервера, которому не нашлось поля, — форма members.ts. */
  const notice = createFieldMessageSlot();

  element.className = 'screen screen-fleet';
  // Исход добавления — единственный сигнал после очистки формы: его объявляет и экранный диктор.
  notice.setAttribute('role', 'status');
  addBlock.className = 'disclosure';
  form.className = 'form';
  kindCaption.className = 'form-field';
  kindCaptionText.className = 'form-field__caption';
  vinCaption.className = 'form-field';
  vinCaptionText.className = 'form-field__caption';
  techCaption.className = 'form-field';
  techCaptionText.className = 'form-field__caption';
  bodyCaption.className = 'form-field';
  bodyCaptionText.className = 'form-field__caption';
  submit.className = 'action action--primary';
  emptyNotice.className = 'list-empty';

  heading.textContent = strings.fleet_title;
  addCaption.textContent = strings.fleet_add;
  kindCaptionText.textContent = strings.fleet_kind_label;
  vinCaptionText.textContent = strings.fleet_vin;
  techCaptionText.textContent = strings.fleet_tech_passport;
  bodyCaptionText.textContent = strings.fleet_body_type_label;
  submit.type = 'submit';
  submit.textContent = strings.fleet_add;
  emptyNotice.textContent = strings.fleet_empty;

  // Имена полей — имена полей тела: по ним сервер называет место, куда
  // положить своё сообщение об отказе.
  kindField.name = 'kind';
  vinField.name = 'vin';
  techField.name = 'tech_passport_number';
  bodyField.name = 'body_type';

  // Выбор вида и типа кузова строится **порождёнными** перечнями контракта:
  // рукописный список разошёлся бы с документом молча. Подпись — по строителю.
  for (const kind of VEHICLE_KINDS) {
    const choice = document.createElement('option');

    choice.value = kind;
    choice.textContent = enumLabel(strings, vehicleKindStringKey(kind));
    kindField.append(choice);
  }

  // Пустой выбор стоит первым: тип кузова тягачу запрещён, и запрет проверяет
  // сервер — форма лишь позволяет не выбирать ничего.
  const noBodyType = document.createElement('option');

  noBodyType.value = '';
  bodyField.append(noBodyType);

  for (const type of BODY_TYPES) {
    const choice = document.createElement('option');

    choice.value = type;
    choice.textContent = enumLabel(strings, bodyTypeStringKey(type));
    bodyField.append(choice);
  }

  // Путь к заявке на занятый VIN — отдельное действие человека, а не вывод из
  // кода отказа: клиент перехода не совершает сам. Адрес собирает единственный
  // строитель.
  claimsLink.className = 'company-link';
  claimsLink.href = vinClaimsPath(companyId);
  claimsLink.textContent = strings.vin_claims_title;

  kindCaption.append(kindCaptionText, kindField, kindMessage);
  vinCaption.append(vinCaptionText, vinField, vinMessage);
  techCaption.append(techCaptionText, techField, techMessage);
  bodyCaption.append(bodyCaptionText, bodyField, bodyMessage);
  form.append(kindCaption, vinCaption, techCaption, bodyCaption, submit);
  addBlock.append(addCaption, form);

  let state: ScreenState | null = 'loading';
  let vehicles: readonly Vehicle[] = [];
  /** Пришёл ли ответ сервера — та же величина, что на экране фирмы. */
  let answered = false;

  function row(vehicle: Vehicle): HTMLElement {
    const node = document.createElement('li');
    const line = document.createElement('span');
    const card = document.createElement('a');
    const service = document.createElement('a');
    const withdrawAction = document.createElement('button');

    node.className = 'vehicle-row';

    // Пустой тип кузова — пустое место: поле пришло и законно пусто у тягача.
    const bodyShown = vehicle.body_type === null ? '' : enumLabel(strings, bodyTypeStringKey(vehicle.body_type));
    // Дата вывода видна у выведенной записи — вывод закрывает запись датой.
    const withdrawnShown = vehicle.withdrawn_at === null ? '' : `${strings.fleet_withdrawn_at} ${momentLabel(activeLocale(), vehicle.withdrawn_at)}`;

    line.textContent = [
      enumLabel(strings, vehicleKindStringKey(vehicle.kind)),
      vehicle.vin,
      vehicle.tech_passport_number,
      bodyShown,
      enumLabel(strings, repairStateStringKey(vehicle.repair_state)),
      enumLabel(strings, vehicleStatusStringKey(vehicle.status)),
      withdrawnShown,
    ].join(' ');

    // Экран сервиса принадлежит записи реестра, и попасть в него можно
    // отсюда: маршрут без единой ссылки существует только для того, кто
    // наберёт адрес руками. Адрес собирает единственный строитель.
    service.className = 'company-link';
    service.href = vehicleServicePath(companyId, vehicle.id);
    service.textContent = strings.service_title;

    // Карточка машины — блок `WEB-2.4`, решение формы `Б2`: ссылка у каждой
    // записи, выведенной тоже — карточка выведенной машины показывается
    // целиком. Адрес собирает единственный строитель.
    card.className = 'company-link';
    card.href = vehiclePath(companyId, vehicle.id);
    card.textContent = strings.vehicle_card_title;

    withdrawAction.type = 'button';
    withdrawAction.className = 'action action--danger';
    withdrawAction.textContent = strings.fleet_withdraw;
    withdrawAction.addEventListener('click', () => {
      void withdraw(vehicle.id);
    });

    // Долг `TD-84`: у выведенной записи «Вывести из реестра» не предлагается —
    // кнопка у уже выведенной выглядела предложением сделать сделанное. Решает
    // поле ответа: вывод закрывает запись датой, и дата пришла — показ по
    // данным сервера, а не правило; повтор вывода сервер по-прежнему принимает.
    if (vehicle.withdrawn_at === null) {
      node.append(line, card, service, withdrawAction);
    } else {
      node.append(line, card, service);
    }

    return node;
  }

  function render(): void {
    notice.textContent = '';
    notice.classList.remove(DONE_NOTICE_CLASS);

    if (state !== null && !answered) {
      // Ответа нет: показывать нечего вовсе. Отказ прав и отсутствие объекта
      // проходят сюда и дают побайтово один экран — нераскрытие.
      element.replaceChildren(renderScreenState(state, strings));

      return;
    }

    const rows: HTMLElement[] = [];

    for (const vehicle of vehicles) {
      rows.push(row(vehicle));
    }

    list.replaceChildren(...rows);
    // Состояние «пусто» принадлежит **списку**, а не экрану: пустой реестр —
    // пришедший ответ, и форма добавления в нём нужнее всего.
    element.replaceChildren(
      heading,
      notice,
      addBlock,
      claimsLink,
      vehicles.length === 0 ? emptyNotice : list,
    );
  }

  /** Запись ответа встаёт в список: по `id`, без второй строки на повтор. */
  function upsertVehicle(received: Vehicle): void {
    const grown: Vehicle[] = [];
    let known = false;

    for (const vehicle of vehicles) {
      if (vehicle.id === received.id) {
        grown.push(received);
        known = true;
      } else {
        grown.push(vehicle);
      }
    }

    if (!known) {
      grown.push(received);
    }

    vehicles = grown;
  }

  /**
   * Исход неудачного **действия** — форма member-rights.ts: осмысленный отказ
   * сервера остаётся на экране с содержимым, отказ без конверта и «нет
   * доступа» принадлежат экрану целиком. Что это за исход, решает единственное
   * место соответствия, а не второй перечень кодов рядом.
   */
  function actionFailed(error: unknown): void {
    const failure = error instanceof ApiFailure ? error : null;
    const asScreen = screenStateFor({
      kind: 'failed',
      code: failure === null ? null : failure.code,
    });

    if (failure === null || failure.code === null || asScreen === 'noAccess') {
      state = asScreen;
      answered = false;
      render();

      return;
    }

    render();

    const homeless = renderFieldMessages(form, failure.details);
    const texts: string[] = [];

    // Сообщение под полем не лежит в закрытом блоке, чьё бы действие его ни принесло.
    if (homeless.length < failure.details.length) {
      addBlock.open = true;
    }

    for (const detail of homeless) {
      texts.push(detail.message);
    }

    if (texts.length === 0 && failure.reason !== null) {
      texts.push(failure.reason);
    }

    notice.textContent = texts.join(' ');
  }

  async function load(): Promise<void> {
    state = screenStateFor({ kind: 'pending' });
    vehicles = [];
    answered = false;
    render();

    try {
      const received = await client.listCompanyVehicles(companyId);

      vehicles = received;
      answered = true;
      state = screenStateFor({ kind: 'done', empty: received.length === 0 });
      addBlock.open = received.length === 0;
    } catch (error) {
      vehicles = [];
      answered = false;
      state = screenStateFor({
        kind: 'failed',
        code: error instanceof ApiFailure ? error.code : null,
      });
    }

    render();
  }

  async function addVehicle(input: AddVehicleInput): Promise<void> {
    // Введённое встаёт в форму: отказ сервера оставляет экран на форме с
    // введённым, и человеку есть что исправлять.
    vinField.value = input.vin;
    techField.value = input.techPassportNumber;
    selectEnumOption(kindField, vehicleKindStringKey(input.kind), vehicleKindStringKey);
    selectEnumOption(bodyField, bodyTypeStringKey(input.bodyType ?? ''), bodyTypeStringKey);
    renderFieldMessages(form, []);

    // Тело — ровно поля VehicleCreateRequest. Тип кузова уходит только
    // выбранным: условную обязательность проверяет сервер.
    const body: VehicleCreateRequest = {
      kind: input.kind as Vehicle['kind'],
      vin: input.vin,
      tech_passport_number: input.techPassportNumber,
    };

    if (typeof input.bodyType === 'string' && input.bodyType !== '') {
      body.body_type = input.bodyType as NonNullable<VehicleCreateRequest['body_type']>;
    }

    try {
      const received = await client.addCompanyVehicle(companyId, body);

      // Перерисовка по ответу: `201` даёт новую строку, `200` — ту же самую,
      // и второй строки на повтор своего VIN не появляется.
      const known = vehicles.some((vehicle) => vehicle.id === received.id);

      upsertVehicle(received);
      state = null;
      render();
      vinField.value = '';
      techField.value = '';
      // То же место уведомления: оно вне блока, который может быть закрыт (`Б5`).
      notice.classList.add(DONE_NOTICE_CLASS);
      notice.textContent = known ? strings.fleet_add_already : strings.fleet_added;
    } catch (error) {
      addBlock.open = true;
      actionFailed(error);
    }
  }

  async function withdraw(vehicleId: string): Promise<void> {
    try {
      await client.withdrawCompanyVehicle(companyId, vehicleId);
    } catch (error) {
      actionFailed(error);

      return;
    }

    // Ответ вывода тела не несёт — список перечитывается, и выведенная запись
    // остаётся в нём со своим статусом и датой.
    try {
      const received = await client.listCompanyVehicles(companyId);

      vehicles = received;
      answered = true;
      state = screenStateFor({ kind: 'done', empty: received.length === 0 });
      render();
    } catch (error) {
      actionFailed(error);
    }
  }

  form.addEventListener('submit', (event) => {
    event.preventDefault();

    void addVehicle({
      kind: kindField.value,
      vin: vinField.value,
      techPassportNumber: techField.value,
      bodyType: bodyField.value === '' ? null : bodyField.value,
    });
  });

  render();

  return {
    element,

    get state(): ScreenState | null {
      return state;
    },

    get vehicles(): readonly Vehicle[] {
      return vehicles;
    },

    load,
    addVehicle,
    withdraw,
  };
}
